import React, { useEffect, useMemo, useRef, useState } from "react";
import { fetchData } from "../../services/api";
import { Link, useNavigate, useParams } from "react-router-dom";
import styles from "./ProcessPaymentByCard.module.scss";
import Icon from "../../components/Icon";
import useVehiclesState from "../../model/useVehiclesState";
import useCardsState from "../../hooks/useCardsState";
import { t } from "i18next";
import useTokenSessionState from "../../model/useTokenSessionState";
import { paymentProcess, tokenPaymentProcess } from "../../model/helpers/Payment";
import FullscreenLoader from "../../components/FullscreenLoader/FullscreenLoader";
import useParkingSession from "../../model/useParkingSession";
import useLocalStorageState from "../../hooks/useLocalStorageState";
import useEmailsState from "../../model/useEmailsState";
import { ParkingSessionService } from "../../services/parkingSessionService";

const ProcessPaymentByCard = (props) => {
    let paramsString = window.location.search;
    const searchParams = useMemo(() => new URLSearchParams(paramsString), [paramsString]);
    const navigate = useNavigate();

    const [caption, setCaption] = useState("paymentDone.caption.emailAndLicencePlateAndCard");

    const fetchInitiated = useRef(false);
    const [paymentInfo, setPaymentInfo] = useState(null);
    const [vehicleExist, setVehicleExist] = useState(false);
    const [cardExist, setCardExist] = useState(false);
    const [emailExist, setEmailExist] = useState(false);

    const [featureCode, setFeatureCode] = useState(null);
    const [featureType, setFeatureType] = useState(null);

    const [vehicleName, setVehicleName] = useState(null);
    const [cardName, setCardName] = useState(null);
    const [lastUsedEmail, setLastUsedEmail] = useLocalStorageState("buy_lastUsedEmail", "");

    const { getByIndexAsync: getVehicleByIndexAsync, add: addVehicle } = useVehiclesState();
    const { getByCardMaskedCLN: getCardByIndexAsync, add: addCard } = useCardsState();
    const { getByIndexAsync: getEmailByIndexAsync, add: addEmail } = useEmailsState();
    const { getByIndexAsync: getTokenSessionState } = useTokenSessionState();
    const {
        add: addParkingSession,
        deleteByIndex: deleteParkingSession,
        getAll: getCurrentParkingSessions,
    } = useParkingSession();
    const { token } = useParams();

    let interval = null;

    const setParkingSession = async (ticketGUID, parkingFrom, parkingTo, ticketData, dateCreated) => {
        await ParkingSessionService.activatePendingSession(
            ticketGUID,
            parkingFrom,
            parkingTo,
            ticketData,
            dateCreated,
            lastUsedEmail,
            getCurrentParkingSessions,
            deleteParkingSession,
            addParkingSession
        );
    };

    const redirectAfterCancel = (ticketData: { sectionCode: any; licensePlate: any; parkMachineCode: any; }) => {
        if (ticketData.sectionCode) {
            navigate(`/buy/pz/${ticketData.sectionCode}/?licencePlate=${ticketData.licensePlate}`);
        }
        if (ticketData.parkMachineCode) {
            navigate(`/buy/pm/${ticketData.parkMachineCode}?licencePlate=${ticketData.licensePlate}`);
        }
        if (!ticketData.sectionCode && !ticketData.parkMachineCode) {
            navigate("/");
        }
    }

    useEffect(() => {
        // Payment canceled by user
        if (searchParams.get("paymentStatus") === "3" || searchParams.get("paymentStatus") === "6") {
            getCurrentParkingSessions().then((allSessions) => {
               const lastSession = allSessions[allSessions.length-1];
               if (lastSession.ticket?.sectionCode) {
                   navigate(`/buy/pz/${lastSession.ticket.sectionCode}?licencePlate=${lastSession.ticket.licensePlate}`);
               }
               if (lastSession.ticket?.parkMachineCode) {
                   navigate(`/buy/pm/${lastSession.ticket.parkMachineCode}?licencePlate=${lastSession.ticket.licensePlate}`);
               }
               if (!lastSession.ticket?.sectionCode && !lastSession.ticket?.parkMachineCode) {
                   navigate("/");
               }
            });

        }

        if (props.zeroPay === true) {
            fetchData(`Tickets/${token}`, "GET").then((data) => {
                setCardExist(true);
                setPaymentInfo({ ticket: data });
                setParkingSession(data.ticketGUID, data.parkingFrom, data.parkingTo, data, data.dateCreated);
            });
        }

        if (props.applepay === true) {
            getTokenSessionState("uuid", token).then((session) => {
                interval = setInterval(() => {
                    try {
                        paymentProcess("Applepay", session.ticketGUID).then((r) => {
                            if (r.paymentSuccessful === true) {
                                clearInterval(interval);
                                setPaymentInfo(r);
                                setParkingSession(
                                    r.ticket.ticketGUID,
                                    r.ticket.parkingFrom,
                                    r.ticket.parkingTo,
                                    r.ticket,
                                    r.ticket.dateCreated
                                );
                                setCardExist(true);
                            }
                            if (r.paymentSuccessful === false && r.paymentInProgress === false) {
                                clearInterval(interval);
                                redirectAfterCancel(r.ticket);
                            }
                        });
                    } catch (e) {
                        clearInterval(interval);
                        navigate(`/`);
                    }
                }, 4000);
            });
        }

        if (props.googlepay === true) {
            getTokenSessionState("uuid", token).then((session) => {
                interval = setInterval(() => {
                    try {
                        paymentProcess("Googlepay", session.ticketGUID).then((r) => {
                            if (r.paymentSuccessful === true) {
                                clearInterval(interval);
                                setPaymentInfo(r);
                                setParkingSession(
                                    r.ticket.ticketGUID,
                                    r.ticket.parkingFrom,
                                    r.ticket.parkingTo,
                                    r.ticket,
                                    r.ticket.dateCreated
                                );
                                setCardExist(true);
                            }
                            if (r.paymentSuccessful === false && r.paymentInProgress === false) {
                                clearInterval(interval);
                                redirectAfterCancel(r.ticket);
                            }
                        });
                    } catch (e) {
                        clearInterval(interval);
                        navigate(`/`);
                    }
                }, 4000);
            });
        }

        if (props.token === true) {
            getTokenSessionState("uuid", token).then((session) => {
                interval = setInterval(() => {
                    try {
                        tokenPaymentProcess(session.ticketGUID, session.paymentGWReference).then((r) => {
                            if (r.paymentSuccessful === true) {
                                clearInterval(interval);
                                setPaymentInfo(r);
                                setParkingSession(
                                    r.ticket.ticketGUID,
                                    r.ticket.parkingFrom,
                                    r.ticket.parkingTo,
                                    r.ticket,
                                    r.ticket.dateCreated
                                );
                            }
                            if (r.paymentSuccessful === false && r.paymentInProgress === false) {
                                clearInterval(interval);
                                redirectAfterCancel(r.ticket);
                            }
                        });
                    } catch (e) {
                        clearInterval(interval);
                        navigate(`/`);
                    }
                }, 4000);
            });
        } else {
            if (
                searchParams.has("payId") &&
                searchParams.has("dttm") &&
                searchParams.has("resultCode") &&
                searchParams.has("resultMessage") &&
                searchParams.has("paymentStatus") &&
                searchParams.has("authCode") &&
                searchParams.has("merchantData") &&
                !paymentInfo &&
                !fetchInitiated.current
            ) {
                fetchInitiated.current = true;
                fetchData("Payment/GatewayResult/Process", "POST", "body", searchParams.toString(), false)
                    .then((results) => {
                        if (results) {
                            setPaymentInfo(results);
                            setParkingSession(
                                results.ticket.ticketGUID,
                                results.ticket.parkingFrom,
                                results.ticket.parkingTo,
                                results.ticket,
                                results.ticket.dateCreated
                            );
                        } else {
                            if (fetchInitiated.current === false) {
                                navigate(`/`);
                            }
                        }
                    })
                    .catch((error) => {
                        console.error("Error fetching payment data:", error);
                    });
            } else {
                if (fetchInitiated.current === true) navigate(`/`);
            }
        }
    }, [searchParams]);

    useEffect(() => {
        if (!paymentInfo) return;

        if (paymentInfo.ticket.sectionCode !== null) {
            setFeatureCode(paymentInfo.ticket.sectionCode);
            setFeatureType("pz");
        } else if (paymentInfo.ticket.parkMachineCode !== null) {
            setFeatureCode(paymentInfo.ticket.parkMachineCode);
            setFeatureType("pm");
        }

        // ověřování jestli má být něco uloženo
        const verifyAndRedirect = async () => {
            const [vehicle, card, email] = await Promise.all([
                getVehicleByIndexAsync("spz", paymentInfo.ticket.licensePlate),
                paymentInfo.ticket.cardMaskedCLN != null
                    ? getCardByIndexAsync(paymentInfo.ticket.cardMaskedCLN)
                    : Promise.resolve(true),
                lastUsedEmail.length ? getEmailByIndexAsync("email", lastUsedEmail) : Promise.resolve(true),
            ]);

            setVehicleExist(!!vehicle);
            setCardExist(!!card);
            setEmailExist(!!email);

            if (vehicle && card && email) {
                navigate(`/paid/${paymentInfo.ticket.ticketGUID}`);
            }
        };

        verifyAndRedirect();
    }, [paymentInfo]);

    useEffect(() => {
        if (!vehicleExist && !cardExist && !emailExist && lastUsedEmail.length) {
            setCaption("paymentDone.caption.emailAndLicencePlateAndCard");
        } else if (!vehicleExist && !cardExist) {
            setCaption("paymentDone.caption.licencePlateAndCard");
        } else if (!vehicleExist && !emailExist && lastUsedEmail.length) {
            setCaption("paymentDone.caption.emailAndLicencePlate");
        } else if (!emailExist && lastUsedEmail.length && !cardExist) {
            setCaption("paymentDone.caption.emailAndCard");
        } else if (!vehicleExist) {
            setCaption("paymentDone.caption.licencePlate");
        } else if (!cardExist) {
            setCaption("paymentDone.caption.card");
        } else if (!emailExist && lastUsedEmail.length) {
            setCaption("paymentDone.caption.email");
        }
    }, [vehicleExist, cardExist, emailExist, lastUsedEmail]);

    const saveOnDevice = () => {
        if (!vehicleExist) {
            addVehicle({
                spz: paymentInfo.ticket.licensePlate,
                name: vehicleName ?? paymentInfo.ticket.licensePlate,
            });
        }

        if (!cardExist) {
            addCard({
                name: cardName && cardName.length ? cardName : `•••• ${paymentInfo.ticket.cardMaskedCLN.slice(-4)}`,
                token: paymentInfo.paymentGWReference,
                cardMaskedCLN: paymentInfo.ticket.cardMaskedCLN,
                cardExpiration: paymentInfo.ticket.cardExpiration,
            });
        }

        if (!emailExist && lastUsedEmail.length) {
            addEmail({
                email: lastUsedEmail,
            });
        }

        navigate(`/paid/${paymentInfo.ticket.ticketGUID}`);
    };

    return paymentInfo === null ? (
        <div className={styles.Wrapper}>
            <FullscreenLoader />
        </div>
    ) : (
        <div className={styles.containerWrapper}>
            <div className={styles.container}>
                <Link to={featureType && featureCode ? `/${featureType}/${featureCode}` : "/"} className={styles.back}>
                    <Icon.CaretLeft size={12} />
                    &nbsp;{t("paymentDone.leave")}
                </Link>
                <h3 className={styles.title}>{t("paymentDone.paymentSuccessful")}</h3>
                <p className={styles.caption}>{t(caption)}</p>
                {!vehicleExist && paymentInfo?.ticket?.licensePlate ? (
                    <div className={styles.item}>
                        <div className={styles.info}>
                            <div className={styles.icon}>
                                <Icon.Car size={22} color={"#1D4F8B"} />
                            </div>
                            <div className={styles.title}>
                                <div className={styles.itemTitle}>{t("paymentDone.car")}</div>
                            </div>
                            <div className={styles.textMuted}>{paymentInfo.ticket.licensePlate}</div>
                            <div className={`${styles.itemEnd} ${styles.itemEndExtended} ${styles.inputMobileMax}`}>
                                <input
                                    required={true}
                                    maxLength={22}
                                    onChange={(evt) => setVehicleName(evt.target.value)}
                                    placeholder={t("paymentDone.nameCar")}
                                    className={styles.input}
                                />
                            </div>
                        </div>
                    </div>
                ) : (
                    ""
                )}
                {!cardExist && !props.token === true && paymentInfo?.ticket?.cardMaskedCLN ? (
                    <div className={styles.item}>
                        <div className={styles.info}>
                            <div className={styles.icon}>
                                <Icon.PaymentCard size={22} color={"#1D4F8B"} />
                            </div>
                            <div className={styles.title}>
                                <div className={styles.itemTitle}>{t("paymentDone.card")}</div>
                            </div>
                            <div className={styles.textMuted}>•••• {paymentInfo.ticket.cardMaskedCLN.slice(-4)}</div>
                            <div className={`${styles.itemEnd} ${styles.itemEndExtended} ${styles.inputMobileMax}`}>
                                <input
                                    onChange={(evt) => setCardName(evt.target.value)}
                                    placeholder={t("paymentDone.nameCard")}
                                    className={styles.input}
                                    maxLength={22}
                                />
                            </div>
                        </div>
                    </div>
                ) : (
                    ""
                )}
                {!emailExist && lastUsedEmail.length ? (
                    <div className={styles.item}>
                        <div className={styles.info}>
                            <div className={styles.icon}>
                                <Icon.Email size={22} color={"#1D4F8B"} />
                            </div>
                            <div className={styles.title}>
                                <div className={styles.itemTitle}>{t("paymentDone.email")}</div>
                            </div>
                            <div className={`${styles.itemEnd} ${styles.itemEndExtended} ${styles.inputMobileMax}`}>
                                <input
                                    required={true}
                                    readOnly={true}
                                    value={lastUsedEmail}
                                    onChange={(evt) => setLastUsedEmail(evt.target.value)}
                                    placeholder={t("paymentDone.email")}
                                    className={styles.input}
                                    maxLength={22}
                                />
                            </div>
                        </div>
                    </div>
                ) : (
                    ""
                )}

                <div className={styles.buttons}>
                    <button className={styles.saveButton} onClick={() => saveOnDevice()}>
                        <Icon.SafeBadge className={styles.actionIcon} size={16} />
                        &nbsp;&nbsp;{t("paymentDone.saveOnThisDevice")}
                    </button>
                    <Link to={`/paid/${paymentInfo.ticket.ticketGUID}`} className={`${styles.saveButton} ${styles.skipButton}`}>
                        {t("paymentDone.skip")}&nbsp;
                        <Icon.CaretRight className={styles.skipIcon} size={16} />
                    </Link>
                </div>
            </div>
        </div>
    );
};
export default ProcessPaymentByCard;
